import React, { useEffect } from "react";
import { Button } from "../../components/Button";
import WelcomeMessage from "../shared/WelcomeMessage";
import { useLocation, useNavigate } from "react-router-dom";
import { AppRoutes } from "../../types/constants.types";
import useFormValues from "../../hooks/useFormValues";

import { loginAPI } from "../../services/AuthService";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../state/store";


const initForm = {
	email: "",
	password: "",
};


export default function LoginForm() {
	const navigate = useNavigate();
	const location = useLocation();
	const { error, isAuthenticated, loading } = useSelector((state: RootState) => state.auth);
	const dispatch = useDispatch<AppDispatch>();
	
	const { values, handleOnChange, refresh } = useFormValues(initForm);
	
	useEffect(() => {
		if (isAuthenticated) {
			navigate(AppRoutes.DASHBOARD);
		}
	}, [navigate, isAuthenticated]);
	
	const handleSubmit = () => {
		if (!values?.email || !values?.password) {
			return;
		}

		dispatch(loginAPI({ email: values.email, password: values.password }))
		.then((resp) => {
			// Auth state is updated automatically by the loginAPI thunk
			if (resp.meta.requestStatus === 'fulfilled') {
				console.log({ loginStatus: 'success', email: values.email })
			}
		})
		.catch(err => {
			console.log({ resp: err, error });
			refresh();
		})
	};

	const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		handleSubmit();
	};

	const isSubmitDisabled = loading || !values?.email || !values?.password;

	return (
		<div className="w-full max-w-[490px]">
			<div className="relative overflow-hidden rounded-[30px] border border-white/70 bg-white/90 p-7 shadow-[0_30px_90px_rgba(2,8,23,0.14)] backdrop-blur-xl dark:border-white/10 dark:bg-slate-950/78 sm:p-9">
				<div className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-emerald-400/70 to-transparent" />
				<div className="pointer-events-none absolute -right-10 top-8 h-28 w-28 rounded-full bg-emerald-200/45 blur-3xl dark:bg-emerald-500/10" />
				<div className="pointer-events-none absolute -left-8 bottom-10 h-24 w-24 rounded-full bg-sky-200/40 blur-3xl dark:bg-sky-500/10" />
				<div className="mb-8 flex items-start justify-between gap-4">
					<WelcomeMessage />
					<span className="rounded-full border border-emerald-200/80 bg-emerald-50/90 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-700 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
						Protected
					</span>
				</div>

				<form className="relative z-10 flex flex-col gap-4" onSubmit={handleFormSubmit}>
					{location.state?.passwordReset && (
						<div className="rounded-2xl border border-emerald-200 bg-emerald-50/95 px-4 py-3 text-sm text-emerald-800 shadow-sm dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200" role="status">
							Your password has been reset. Sign in to the E2G Admin Portal with your new password.
						</div>
					)}
					{error && (
						<div className="rounded-2xl border border-red-200 bg-red-50/95 px-4 py-3 text-sm text-red-700 shadow-sm dark:border-red-400/30 dark:bg-red-900/20 dark:text-red-200" role="alert">
							<span className="block sm:inline">{error}</span>
						</div>
					)}

					<div className="space-y-2">
						<label htmlFor="email" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
							Email address
						</label>
						<input
							id="email"
							type="email"
							autoFocus
							autoComplete="email"
							placeholder="Enter your email address"
							value={values?.email || ''}
							onChange={(e) => handleOnChange("email", e.target.value)}
							className="w-full rounded-[18px] border border-slate-200/90 bg-white/95 px-4 py-3.5 text-slate-900 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_10px_30px_rgba(15,23,42,0.03)] transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 dark:border-white/10 dark:bg-slate-950/76 dark:text-slate-100 dark:placeholder:text-slate-500"
						/>
					</div>

					<div className="space-y-2">
						<label htmlFor="password" className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
							Password
						</label>
						<input
							id="password"
							type="password"
							autoComplete="current-password"
							placeholder="Enter your password"
							value={values?.password || ''}
							onChange={(e) => handleOnChange("password", e.target.value)}
							className="w-full rounded-[18px] border border-slate-200/90 bg-white/95 px-4 py-3.5 text-slate-900 outline-none shadow-[inset_0_1px_0_rgba(255,255,255,0.85),0_10px_30px_rgba(15,23,42,0.03)] transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-500/10 dark:border-white/10 dark:bg-slate-950/76 dark:text-slate-100 dark:placeholder:text-slate-500"
						/>
					</div>

					<div className="flex flex-col gap-3 pt-1 sm:flex-row sm:items-center sm:justify-between">
						<label className="inline-flex items-center gap-2 cursor-pointer text-sm text-slate-700 dark:text-slate-300">
							<input
								type="checkbox"
								className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 dark:border-white/20"
							/>
							<span>Remember me</span>
						</label>

						<button
							type="button"
							onClick={() => navigate(AppRoutes.FORGOT_PASSWORD)}
							className="text-sm font-medium text-emerald-700 transition-colors hover:text-emerald-600 dark:text-emerald-300"
						>
							Forgot password?
						</button>
					</div>

					<div className="pt-3">
						<Button
							variant="primary"
							value="Sign in"
							type="submit"
							size="md"
							fullWidth={true}
							loading={loading}
							disabled={isSubmitDisabled}
							className="rounded-[18px] py-3.5 text-sm uppercase tracking-[0.2em] shadow-[0_20px_38px_rgba(16,185,129,0.3)]"
						/>
					</div>

					<p className="pt-1 text-xs text-slate-500 dark:text-slate-400">
						Press Enter to submit your credentials.
					</p>
				</form>
			</div>
		</div>
	);
}
